/**
 * AuthContext - Authentication state management
 * 
 * Provides authentication state and methods to the entire application.
 * 
 * Security features:
 * - Access tokens stored in memory only (never localStorage - XSS protection)
 * - Refresh tokens stored in HttpOnly cookies (automatic, not accessible to JS)
 * - Auto token refresh on app load (silent authentication)
 * - Auto token refresh before expiry (120s before)
 * - Proper cleanup on logout
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { AuthContext, type AuthContextType, type User } from './auth'
import axios from 'axios'
import env from '../config/env'
import { registerTokenGetter, registerTokenRefresher } from '../services/tokenManager'


// API base URL
const API_BASE_URL = env.API_URL || (typeof window !== 'undefined' ? window.location.origin : '')

// Create axios instance for auth requests (with credentials for cookies)
const authApi = axios.create({
  baseURL: `${API_BASE_URL}/api/auth`,
  timeout: 30000,
  withCredentials: true, // Important: sends HttpOnly cookies
  headers: {
    'Content-Type': 'application/json',
  },
})

interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * AuthProvider - Provides authentication context to the app
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  // Ref to track refresh timeout
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  /**
   * Load user info from /me endpoint
   */
  const loadUser = useCallback(async (token: string) => {
    try {
      const response = await authApi.get<User>('/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setUser(response.data)
    } catch {
      // Failed to load user - clear state
      setAccessToken(null)
      setUser(null)
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }
    }
  }, [])
  
  /**
   * Refresh access token using refresh token cookie
   */
  const refreshToken = useCallback(async () => {
    try {
      const response = await authApi.post<{ access_token: string; token_type: string }>('/refresh')
      const newToken = response.data.access_token
      setAccessToken(newToken)
      
      // Reload user info for consistency
      await loadUser(newToken)
      
      // Schedule next refresh (15 min = 900s, refresh 120s before = 780s)
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
      refreshTimeoutRef.current = setTimeout(() => {
        refreshToken()
      }, 780000) // 13 minutes
    } catch {
      // Refresh failed - user needs to login again
      setAccessToken(null)
      setUser(null)
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }
    }
  }, [loadUser])
  
  /**
   * Schedule token refresh before expiry
   */
  const scheduleTokenRefresh = useCallback((delayMs: number) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }
    
    refreshTimeoutRef.current = setTimeout(() => {
      refreshToken()
    }, delayMs)
  }, [refreshToken])
  
  
  /**
   * Register token getter and refresher with API client
   * This allows api.ts to access tokens without circular dependencies
   */
  useEffect(() => {
    registerTokenGetter(() => accessToken)
    registerTokenRefresher(refreshToken)
  }, [accessToken, refreshToken])
  
  /**
   * Silent authentication on app load
   * Uses the shared refreshToken() path so it deduplicates with api.ts interceptor
   */
  useEffect(() => {
    let mounted = true

    const attemptSilentAuth = async () => {
      try {
        await refreshToken()
        // refreshToken already sets accessToken, user, and schedules next refresh
      } catch {
        // No valid refresh token - user needs to login
        if (mounted) {
          setAccessToken(null)
          setUser(null)
        }
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    attemptSilentAuth()

    return () => {
      mounted = false
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [refreshToken])
  
  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await authApi.post<{ access_token: string; token_type: string }>('/login', {
        email,
        password,
      })
      
      const token = response.data.access_token
      setAccessToken(token)
      
      // Load user info
      await loadUser(token)
      
      // Schedule next refresh
      scheduleTokenRefresh(780000) // 13 minutes
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || 'Login failed'
        throw new Error(message)
      }
      throw error
    }
  }, [loadUser, scheduleTokenRefresh])
  
  /**
   * Register with email, password, and invitation token
   */
  const register = useCallback(async (email: string, password: string, inviteToken: string) => {
    try {
      const response = await authApi.post<{ access_token: string; token_type: string }>('/register', {
        email,
        password,
        invite_token: inviteToken,
      })
      
      const token = response.data.access_token
      setAccessToken(token)
      
      // Load user info
      await loadUser(token)
      
      // Schedule next refresh
      scheduleTokenRefresh(780000) // 13 minutes
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || 'Registration failed'
        throw new Error(message)
      }
      throw error
    }
  }, [loadUser, scheduleTokenRefresh])
  
  /**
   * Logout - revoke refresh token and clear state
   */
  const logout = useCallback(async () => {
    try {
      // Revoke refresh token on server (uses HttpOnly cookie automatically)
      if (accessToken) {
        await authApi.post('/logout', {}, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
      }
    } catch {
      // Continue with logout even if server call fails
    } finally {
      // Clear local state
      setAccessToken(null)
      setUser(null)
      
      // Clear refresh timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }
    }
  }, [accessToken])
  
  /**
   * Reload user data using current access token
   */
  const reloadUser = useCallback(async () => {
    if (accessToken) {
      await loadUser(accessToken)
    }
  }, [accessToken, loadUser])

  const value: AuthContextType = {
    user,
    accessToken,
    isAuthenticated: !!accessToken && !!user,
    isLoading,
    login,
    register,
    logout,
    refreshToken,
    reloadUser,
  }
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}


